import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import {
  PUBLICATION_CONTENT_KIND,
  PUBLICATION_INDEX_KIND,
  getPublicationRefs,
  isValidPublicationIndex,
  isValidPublicationSection,
} from '@/lib/nkbip01';

/** A single publication index event, addressed by pubkey + `d` tag. */
export function usePublicationIndex(pubkey: string | undefined, d: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['nkbip01', 'index', pubkey, d],
    queryFn: async (c) => {
      if (!pubkey || !d) return null;
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);
      const events = await nostr.query(
        [{ kinds: [PUBLICATION_INDEX_KIND], authors: [pubkey], '#d': [d], limit: 5 }],
        { signal },
      );
      const valid = events.filter(isValidPublicationIndex).sort((a, b) => b.created_at - a.created_at);
      return valid[0] ?? null;
    },
    enabled: Boolean(pubkey && d),
    staleTime: 60_000,
  });
}

/**
 * Resolves a publication index's ordered `a` tags into their target
 * events (sections, or nested indices). Missing/invalid targets are
 * dropped rather than breaking the whole publication.
 */
export function usePublicationSections(index: NostrEvent | null | undefined) {
  const { nostr } = useNostr();
  const refs = index ? getPublicationRefs(index) : [];
  const refsKey = refs.map((r) => `${r.pointer.kind}:${r.pointer.pubkey}:${r.pointer.identifier}`).join(',');

  return useQuery({
    queryKey: ['nkbip01', 'sections', index?.id, refsKey],
    queryFn: async (c) => {
      if (refs.length === 0) return [];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(6000)]);

      // Group by kind so we can issue one filter per kind rather than one
      // REQ per reference.
      const byKind = new Map<number, { pubkey: string; identifier: string }[]>();
      for (const ref of refs) {
        const list = byKind.get(ref.pointer.kind) ?? [];
        list.push({ pubkey: ref.pointer.pubkey, identifier: ref.pointer.identifier });
        byKind.set(ref.pointer.kind, list);
      }

      const filters = [...byKind.entries()].map(([kind, pointers]) => ({
        kinds: [kind],
        authors: [...new Set(pointers.map((p) => p.pubkey))],
        '#d': [...new Set(pointers.map((p) => p.identifier))],
        limit: pointers.length * 3 + 10,
      }));

      const events = await nostr.query(filters, { signal });

      // Index fetched events by (kind, pubkey, d) so we can look them up in
      // the index's original display order.
      const byKey = new Map<string, NostrEvent>();
      for (const event of events) {
        const d = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
        const key = `${event.kind}:${event.pubkey}:${d}`;
        const existing = byKey.get(key);
        if (!existing || event.created_at > existing.created_at) byKey.set(key, event);
      }

      return refs
        .map((ref) => byKey.get(`${ref.pointer.kind}:${ref.pointer.pubkey}:${ref.pointer.identifier}`))
        .filter((event): event is NostrEvent => {
          if (!event) return false;
          if (event.kind === PUBLICATION_CONTENT_KIND) return isValidPublicationSection(event);
          if (event.kind === PUBLICATION_INDEX_KIND) return isValidPublicationIndex(event);
          return true; // 30817/30818/30023 leaves — no NKBIP-01-specific validation.
        });
    },
    enabled: Boolean(index) && refs.length > 0,
    staleTime: 60_000,
  });
}

/** A single publication section, addressed by pubkey + `d` tag. */
export function usePublicationSection(pubkey: string | undefined, d: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['nkbip01', 'section', pubkey, d],
    queryFn: async (c) => {
      if (!pubkey || !d) return null;
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);
      const events = await nostr.query(
        [{ kinds: [PUBLICATION_CONTENT_KIND], authors: [pubkey], '#d': [d], limit: 5 }],
        { signal },
      );
      const valid = events.filter(isValidPublicationSection).sort((a, b) => b.created_at - a.created_at);
      return valid[0] ?? null;
    },
    enabled: Boolean(pubkey && d),
    staleTime: 60_000,
  });
}

/** Recent publication indices across all authors, for a browse/discovery feed. */
export function useRecentPublications(limit = 24) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['nkbip01', 'recent', limit],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);
      const events = await nostr.query([{ kinds: [PUBLICATION_INDEX_KIND], limit: limit * 3 }], { signal });
      const valid = events.filter(isValidPublicationIndex);

      const byKey = new Map<string, NostrEvent>();
      for (const event of valid) {
        const d = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
        const key = `${event.pubkey}:${d}`;
        const existing = byKey.get(key);
        if (!existing || event.created_at > existing.created_at) byKey.set(key, event);
      }

      return [...byKey.values()].sort((a, b) => b.created_at - a.created_at).slice(0, limit);
    },
    staleTime: 30_000,
  });
}

/** All publication indices authored by a specific pubkey. */
export function usePublicationsByAuthor(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['nkbip01', 'by-author', pubkey],
    queryFn: async (c) => {
      if (!pubkey) return [];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);
      const events = await nostr.query([{ kinds: [PUBLICATION_INDEX_KIND], authors: [pubkey], limit: 100 }], { signal });
      const valid = events.filter(isValidPublicationIndex);

      const byD = new Map<string, NostrEvent>();
      for (const event of valid) {
        const d = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
        const existing = byD.get(d);
        if (!existing || event.created_at > existing.created_at) byD.set(d, event);
      }
      return [...byD.values()].sort((a, b) => b.created_at - a.created_at);
    },
    enabled: Boolean(pubkey),
    staleTime: 60_000,
  });
}
